import { css, useTheme } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { updateProject } from '@/lib/api-client';
import { fontSizes } from '@/theme/tokens';

interface ScriptTitleProps {
  projectId: string;
  initialTitle: string;
  onManualEdit?: () => void;
}

export function ScriptTitle({ projectId, initialTitle, onManualEdit }: ScriptTitleProps) {
  const theme = useTheme();
  const [title, setTitle] = useState(initialTitle);
  const savedTitle = useRef(initialTitle);
  const cancelledRef = useRef(false);

  useEffect(() => {
    setTitle(initialTitle);
    savedTitle.current = initialTitle;
  }, [initialTitle]);

  const save = useCallback(async () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const trimmed = title.trim();
    if (!trimmed || trimmed === savedTitle.current) return;
    try {
      await updateProject(projectId, { title: trimmed });
      savedTitle.current = trimmed;
    } catch {
      // revert on error
      setTitle(savedTitle.current);
    }
  }, [projectId, title]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(e.target.value);
      onManualEdit?.();
    },
    [onManualEdit],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      cancelledRef.current = true;
      setTitle(savedTitle.current);
      e.currentTarget.blur();
    }
  }, []);

  return (
    <input
      type="text"
      aria-label="Script title"
      value={title}
      onChange={handleChange}
      onBlur={save}
      onKeyDown={handleKeyDown}
      css={css`
        font-size: ${fontSizes.xl};
        font-weight: 600;
        color: ${theme.colors.text};
        background: transparent;
        border: none;
        border-bottom: 2px solid transparent;
        border-radius: 0;
        padding: 0;
        margin: 0;
        width: 100%;
        outline: none;
        font-family: inherit;
        transition: border-color 0.15s ease;

        &:hover {
          border-bottom-color: ${theme.colors.border};
        }

        &:focus {
          border-bottom-color: ${theme.colors.primary};
        }
      `}
    />
  );
}
